import { Module } from 'lib/vendor/terrific/Module';
import { Hook, Sandbox } from 'lib/vendor/terrific/Sandbox';
import * as jQuery from 'jquery';
import { FilterConfig } from 'lib/sdx/gcr-pressrelease/GcrPressReleaseFilterConfig';
import { FilterTag } from './FilterTag';
import { readStateFromHash } from 'lib/util/url';

const autoSubmitTimeout = 500; // In ms

export default class GcrNewsArchiveSearch extends Module {

	// Language determined from HTML.lang
	private readonly lang : 'de' | 'en' | 'fr' | 'it';


	/* scheduled auto submit timer */
	private scheduledSubmit : number;
	private path! : HTMLInputElement;
	private query! : HTMLInputElement;
	private category! : HTMLInputElement;
	private section! : HTMLInputElement;
	private period! : HTMLInputElement;
	private startDate! : HTMLInputElement;
	private endDate! : HTMLInputElement;

	private breadcrumbs! : HTMLInputElement;

	private filterOpenState = false;
	private filterTag : FilterTag;

	constructor($ctx : JQuery, sandbox : Sandbox, id : number, modName : string) {

		super($ctx, sandbox, id, modName);

		/* Read language from page attribute */
		const lang = jQuery('HTML').attr('lang');
		switch ((lang || 'de').toLocaleLowerCase()) {
			case 'en':
				this.lang = 'en';
				break;
			case 'fr':
				this.lang = 'fr';
				break;
			case 'it':
				this.lang = 'it';
				break;
			default:
				this.lang = 'de';
				break;
		}

		this.scheduledSubmit = 0;
		// Key for the hash state
		this.hashKey = 'NewsArchive';

		this.path = this.$ctx.find('input[name=path]')[0] as HTMLInputElement;
		this.query = this.$ctx.find('[name=news-query]')[0] as HTMLInputElement;
		this.category = this.$ctx.find('[name=news-topic]')[0] as HTMLInputElement;
		this.section = this.$ctx.find('[name=news-section]')[0] as HTMLInputElement;
		this.period = this.$ctx.find('[name=period]')[0] as HTMLInputElement;
		this.startDate = this.$ctx.find('[name=start-date]')[0] as HTMLInputElement;
		this.endDate = this.$ctx.find('[name=end-date]')[0] as HTMLInputElement;
		this.breadcrumbs = this.$ctx.find('.filter-breadcrumbs')[0] as HTMLInputElement;

		this.filterTag = new FilterTag(
			this.breadcrumbs,
			this.$ctx.data('sections'),
			this.$ctx.data('topics'),
			this.$ctx.data('periods'),
		);
	}

	public on(finished : Hook) {
		this.$ctx.find('.gcr-archive-collapsible-head').on('click', this.changeFilterState.bind(this));
		finished();
	}

	public moduleStateChanged(hashState : Record<string, any>) : void {
		this.readHashState(hashState);
		this.submitForm(false, this.getFilterConfig());
	}

	public after(hashState : Record<string, any>) {
		let updateHashState = true;
		if (jQuery.isEmptyObject(hashState)) {
			if (this.$ctx.data('default-sections')) {
				this.setSections(this.$ctx.data('default-sections').split(','));
			}
			this.period.value = ['1'] as any;
			this.handleDateFields();

		} else {
			this.readHashState(hashState);
			updateHashState = false;
		}
		const filter = this.getFilterConfig();
		this.renderBreadcrumbBar(filter);
		this.submitForm(updateHashState, filter);
		this.setupAutoSubmit();
	}

	private readHashState(hashState : Record<string, any>) {
		if (hashState.query) {
			this.query.value = hashState.query;
		}
		if (hashState.section) {
			this.section.value = hashState.section;
		}

		if (hashState.category) {
			this.category.value = hashState.category;
		}

		if (hashState.period) {
			this.period.value = hashState.period;
		} else {
			this.period.value = ['1'] as any;
		}

		if (hashState.startDate) {
			this.startDate.value = hashState.startDate;
		}

		if (hashState.endDate) {
			this.endDate.value = hashState.endDate;
		}

		if (hashState.filterState && (hashState.filterState === 'true') !== this.filterOpenState) {
			this.changeFilterState();
		}
		this.handleDateFields();
	}

	private changeFilterState() {
		this.$ctx.find('.gcr-archive-collapsible').toggleClass('gcr-archive-collapsible-closed');
		this.filterOpenState = !this.filterOpenState;
		const state = readStateFromHash(this.hashKey, true) || {};
		this.setModuleState({...state, filterState: this.filterOpenState});
	}

	private setSections(data : string[]) {
		this.section.value = data as any;
	}

	private submitForm(updateHashState : boolean, filter : FilterConfig) {

		// Dispatch to all listening modules
		this.fire('updateSearchFilter', filter);
		// Update our hash state if needed
		if (updateHashState) {
			this.setModuleState({
				category: filter.category,
				endDate: filter.endDate,
				filterState: this.filterOpenState,
				period: filter.period,
				query: filter.query,
				section: filter.section,
				startDate: filter.startDate,
			});
		}
		// Reset old auto submit
		this.clearAutoSubmit();
	}

	private setupAutoSubmit() {

		(this.query as any).changeCallback = this.changeCallback.bind(this);
		(this.category as any).changeCallback = this.changeCallback.bind(this);
		(this.section as any).changeCallback = this.changeCallback.bind(this);
		(this.period as any).changeCallback = this.changeCallback.bind(this);
		(this.startDate as any).changeCallback = this.changeCallback.bind(this);
		(this.endDate as any).changeCallback = this.changeCallback.bind(this);

	}

	private getFilterConfig() : FilterConfig {
		const filter : FilterConfig = {lang: this.lang, _charset_: 'UTF-8'};
		if (this.query) {
			filter.query = this.query.value;
		}
		if (this.path) {
			filter.path = this.path.value;
		}
		if (this.period) {
			filter.period = this.period.value;
		}
		if (this.startDate) {
			filter.startDate = this.startDate.value;
		}
		if (this.endDate) {
			filter.endDate = this.endDate.value;
		}
		if (this.category) {
			filter.category = this.category.value as any;
		}
		if (this.section) {
			filter.section = this.section.value as any;
		}

		return filter;
	}

	private changeCallback() {
		this.clearAutoSubmit();
		this.handleDateFields();
		const filter = this.getFilterConfig();
		this.renderBreadcrumbBar(filter);

		// Schedule auto submit after some timeout
		this.scheduledSubmit = setTimeout(() => {
			this.submitForm(true, filter);
		}, autoSubmitTimeout);
	}

	private clearAutoSubmit() {
		// Unscheduled previous auto submit
		clearTimeout(this.scheduledSubmit);
		// Clear status variable
		this.scheduledSubmit = 0;
	}

	private handleDateFields() {
		if (this.period?.value[0] === '5') {
			this.$ctx.find('.news-date').show();
		} else {
			this.$ctx.find('.news-date').hide();
		}

	}

	private closeBreadcrumbCallback(group : string, tag : string) {
		if (group === 'sections') {
			const val : string[] = (this.section as any).value.slice();
			val.splice(val.indexOf(tag), 1);
			this.section.value = val as any;
		}
		if (group === 'topics') {
			const val : string[] = (this.category as any).value.slice();
			val.splice(val.indexOf(tag), 1);
			this.category.value = val as any;
		}
		if (group === 'query') {
			this.query.value = '';
		}
	}

	private renderBreadcrumbBar(filter : FilterConfig) {
		this.filterTag.renderBreadcrumb(
			filter.section || [],
			filter.category || [],
			filter.period || '',
			filter.query || '',
			this.closeBreadcrumbCallback.bind(this),
		);

	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/sdx/gcr-pressrelease/Tc.Module.GcrNewsArchiveSearch.ts